import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdClose } from "react-icons/md";
import * as yup from "yup";
import { PaziaFormButton, PaziaInput, PaziaLoader } from "components/atoms";
import { useContactFormSettings, shopApi, ContactFormSettings } from "api/shop";
import { useToaster } from "utils";
import { useState, useEffect } from "react";

const FORM_NAMES = {
  NOTIFICATION_EMAILS: "notificationEmails",
} as const;

const emailSchema = yup.object({
  value: yup.string().required("Email jest wymagany").email("Nieprawidłowy email"),
});

const schema = yup
  .object({
    [FORM_NAMES.NOTIFICATION_EMAILS]: yup.array().of(emailSchema).default([]),
  })
  .required();

interface FormValues {
  notificationEmails: Array<{ value: string }>;
}

export const ShopContactFormSettingsForm = () => {
  const { contactFormSettings, isLoading, invalidate } = useContactFormSettings();
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.NOTIFICATION_EMAILS]:
        contactFormSettings?.notificationEmails?.map((e) => ({ value: e })) ?? [],
    },
  });
  const { handleSubmit, reset, control } = methods;

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: FORM_NAMES.NOTIFICATION_EMAILS,
  });

  useEffect(() => {
    if (contactFormSettings) {
      reset({
        [FORM_NAMES.NOTIFICATION_EMAILS]:
          contactFormSettings.notificationEmails?.map((e) => ({ value: e })) ?? [],
      });
    }
  }, [contactFormSettings, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      const formattedData: ContactFormSettings = {
        notificationEmails: data.notificationEmails.map((e) => e.value),
      };
      await shopApi.updateContactFormSettings(formattedData);
      showSuccess("Zapisano ustawienia formularza kontaktowego");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać ustawień formularza kontaktowego");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-3xl gap-6">
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">
                Emaile do powiadomień
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Adresy, na które będą wysyłane powiadomienia o nowych zgłoszeniach z formularza kontaktowego.
              </p>
            </div>
            <button
              type="button"
              onClick={() => appendEmail({ value: "" })}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj email
            </button>
          </div>

          {emailFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak emaili do powiadomień.
            </p>
          ) : (
            <div className="space-y-2">
              {emailFields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-2">
                  <div className="flex-1">
                    <PaziaInput
                      name={`${FORM_NAMES.NOTIFICATION_EMAILS}.${index}.value`}
                      placeholder="np. biuro@example.com"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeEmail(index)}
                    className="mt-1 rounded p-1 text-slate-400 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń email"
                  >
                    <MdClose size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving} />
        </div>
      </form>
    </FormProvider>
  );
};
